import React, { useState } from 'react';
import { Form, Input, Card, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from 'utils/validators';
import CustomButton from 'components/Common/ui/CustomButton';
import CustomAlert from 'components/Common/ui/CustomAlert';

interface EmailSearchComponentProps {
    onEmailFound: (userData: any) => Promise<any> | void;
    onEmailNotFound: (email: string) => Promise<any> | void;
    searchService: (params: { email: string, role?: string }) => Promise<any> | void;
    role?: string;
    placeholder?: string;
    title?: string;
    helpText?: string;
}

const EmailSearchComponent: React.FC<EmailSearchComponentProps> = ({
    onEmailFound,
    onEmailNotFound,
    searchService,
    role,
    placeholder,
    title,
    helpText
}) => {
    const { t } = useTranslation();
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchedEmail, setSearchedEmail] = useState<string>('');

    const handleEmailSearch = async () => {
        if (!searchedEmail || !isValidEmail(searchedEmail)) {
            message.error(t("validation.please_enter_valid_email"));
            return;
        }

        setSearchLoading(true);

        try {
            const response = await searchService({ email: searchedEmail, role: role });

            if (response) {
                await onEmailFound(response);
                message.info(t("teacherPage.teacher_found_data_prefilled"));
            } else {
                await onEmailNotFound(searchedEmail);
                message.info(t("teacherPage.email_not_found_continue_creating"));
            }
        } catch (error) {
            console.error("Search error:", error);
            message.error(t("failed_to_search_email"));
        } finally {
            setSearchLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEmailSearch();
        }
    };

    return (
        <Card title={title || t("teacherPage.search_teacher_by_email")} className="mb-4">
            <div className="row">
                <div className="col-md-8">
                    <Form.Item
                        label={t("email")}
                        rules={[
                            { required: true, message: t("validation.email_required") },
                            { type: "email", message: t("validation.please_enter_valid_email") },
                        ]}
                    >
                        <Input
                            placeholder={placeholder || t("enter_email")}
                            onChange={(e) => setSearchedEmail(e.target.value)}
                            value={searchedEmail}
                            onKeyPress={handleKeyPress}
                            disabled={searchLoading}
                        />
                    </Form.Item>
                </div>
                <div className="col-md-4 align-items-end">
                    <CustomButton
                        onClick={handleEmailSearch}
                        disabled={searchLoading || !searchedEmail}
                        className="w-100"
                    >
                        <i className="ti ti-search me-2" />
                        {searchLoading ? t("searching") : t("search")}
                    </CustomButton>
                </div>
            </div>
            <CustomAlert
                message=""
                type="info"
                showIcon
                description={helpText || t("teacherPage.teacher_email_search_help")}
            />
        </Card>
    );
};

export default EmailSearchComponent;